// <vf-income-explorer>: operator-facing drill-down instrument. One bordered
// frame (single-frame law), four levels rendered in place:
//   1. epochs   — stacked bars of the gross income channels (/income + /apy)
//   2. blocks   — bars per produced block of one epoch (/blocks, paginated)
//   3. block    — payer-grouped stored transactions (/blocks/{slot}/txs)
//   4. tx       — archival decode of one transaction (/tx/{signature})
//
// Attributes: api-base (required), from-epoch, to-epoch, theme (light|dark),
// embedded (presence-only: suppresses this element's outer frame when it is
// nested inside another widget's framed .vf-root — single-frame law; inner
// layout is unchanged).
// Descend by clicking a bar or a table row; ascend via breadcrumb crumbs or
// Esc (one level). Embedded, the header carries an [x] close control; it
// and a top-level Esc dispatch the same composed close event
// (EXPLORER_CLOSE_EVENT) for the host to route to its explorer toggle.
// Fetches are per level on demand, aborted on navigation
// and disconnect; every level has factual loading, error (with retry), and
// written-absence states.

import { closeIcon } from "./icons";
import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import type { EChartsType } from "echarts/core";

import { ApiError, parseApyRows, parseIncomeRows } from "./api";
import { formatCount } from "./format";
import {
  ascend,
  blocksHeaderLine,
  breadcrumbs,
  coverageLine,
  createFetchJson,
  EPOCHS_FOOTNOTE,
  EXPLORER_CLOSE_EVENT,
  fetchEpochBlocks,
  groupByPayer,
  NO_INCOME_TEXT,
  NO_STORED_TXS_TEXT,
  noBlocksText,
  parseBlockTxs,
  parseTxDetail,
  type BlockTxsData,
  type ExplorerBlockRow,
  type ExplorerLevel,
  type FetchJson,
  type PayerGroup,
  type TxDetailData,
} from "./income-explorer";
import {
  blockTooltipLines,
  buildBlocksOption,
  buildEpochsOption,
  epochTooltipTitle,
  epochTooltipView,
  joinEpochChannels,
  type EpochChannelPoint,
} from "./income-explorer-chart";
import { renderPayerTable, renderTxDetail } from "./income-explorer-views";
import { STYLES } from "./styles";
import { themeTokens, type ThemeTokens } from "./theme";
import { viewToHtml, type EpochTooltipView, type TooltipLine } from "./tooltip";

echarts.use([BarChart, GridComponent, LegendComponent, TooltipComponent, SVGRenderer]);

// Explorer-local additions to the shared stylesheet: breadcrumb header,
// chart height, tables, identifier wrapping, log scroll block. Everything
// else reuses STYLES classes and tokens.
// Host width below which the identifier cells trade wrap-anywhere for an
// ellipsis (EXPLORER_STYLES narrow rules). Matches the main widget's
// header-wrap rung so the two surfaces cross into their narrow forms
// together. Own ResizeObserver measurement, same no-media-query law.
const EXPLORER_NARROW_PX = 620;

const EXPLORER_STYLES = `
.vf-root.vf-explorer .vf-header { height: auto; min-height: 40px; flex-wrap: wrap; padding: 8px 4px; }
.vf-crumbs { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.vf-crumb-current { font-size: 13px; font-weight: 600; }
.vf-crumb-sep { color: var(--vf-muted); }
.vf-explorer-chart { width: 100%; height: 320px; }
.vf-explorer-chart:focus-visible { outline: 2px solid var(--vf-accent); outline-offset: -2px; }
.vf-explorer-subhead { padding: 8px 4px 0; font-size: 12px; color: var(--vf-muted); }
/* overflow-x is the single-frame law's last line of defense: a table or
   pre-formatted block wider than the host must scroll INSIDE the frame,
   never bleed through the card border (tx-detail instructions did, via
   unbreakable 60ch address lines forcing the table past the host). */
.vf-explorer-content { padding: 8px 4px; overflow-x: auto; }
.vf-explorer-content table { width: 100%; border-collapse: collapse; }
.vf-explorer-content th,
.vf-explorer-content td { text-align: left; vertical-align: top; padding: 4px 8px 4px 0; border-bottom: 1px solid var(--vf-border); font-size: 12px; }
.vf-explorer-content th { font-weight: 600; color: var(--vf-muted); }
.vf-explorer-content td.vf-mono { font-size: 13px; }
/* Label column sizes to its own content (1% + nowrap = shrink-to-fit
   table column) instead of a fixed 160px gutter that wasted width on
   short labels; the narrow rung swaps to a fixed compact column and
   lets long labels wrap. */
.vf-fact-label { color: var(--vf-muted); width: 1%; white-space: nowrap; padding-right: 16px; }
.vf-ident-wrap { display: flex; align-items: flex-start; gap: 8px; }
/* Identifiers are HANDLES, not documents (operator direction
   2026-07-17): one line always, ellipsis when space runs out — a
   wrapped multi-line pubkey/signature block reads as debris. 44ch shows
   a full pubkey at desk widths while an 87-char signature truncates to
   a clean handle; the narrow rung tightens the handle. The real value
   stays reachable everywhere: the copy control sits beside every
   identifier and the row itself descends to the tx detail. */
.vf-ident {
  font-size: 12px;
  max-width: 44ch;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.vf-copy { flex: none; }
tr.vf-payer-row, tr.vf-tx-row { cursor: pointer; }
tr.vf-payer-row:focus-visible, tr.vf-tx-row:focus-visible { outline: 2px solid var(--vf-accent); outline-offset: -2px; }
.vf-tx-inner { margin: 4px 0 8px; }
.vf-explorer-section-label { font-size: 11px; font-weight: 500; color: var(--vf-muted); margin: 16px 0 4px; }
.vf-tx-detail .vf-explorer-section-label:first-child { margin-top: 0; }
.vf-absence { font-size: 12px; color: var(--vf-muted); padding: 8px 0; }
.vf-log { font-size: 11px; line-height: 1.5; max-height: 240px; overflow: auto; border: 1px solid var(--vf-border); border-radius: 3px; padding: 8px; white-space: pre; }
.vf-ins-detail-body { display: flex; flex-direction: column; gap: 6px; padding: 2px 0 6px 12px; }
.vf-ins-decoded { font-size: 12px; }
.vf-ins-parsed-info { max-height: 160px; }
.vf-ins-data-facts { font-size: 11px; color: var(--vf-muted); }
.vf-explorer-footnote { padding: 8px 4px 0; font-size: 11px; color: var(--vf-muted); line-height: 1.35; max-width: 72ch; }
/* Embedded close control: rightmost in the header row, same [x] .vf-btn
   grammar as the drill-down panel's close. Standalone mode has no host
   surface to return to, so the control hides there. */
.vf-explorer-close { margin-left: auto; }
.vf-root.vf-explorer:not(.vf-embedded) .vf-explorer-close { display: none; }
/* Embedded: the HOST header already titles the surface ("Operator income
   drill-down"), so this element's own title row would stack a second,
   near-identical line. Scoped to the embedded explorer root only — the
   standalone header is untouched, and the host's .vf-header-title lives
   in a different shadow root. */
.vf-root.vf-explorer.vf-embedded .vf-header-title,
.vf-root.vf-explorer.vf-embedded .vf-header-sep { display: none; }
/* Pure-numeric columns (txs, fees/tips/CU sums, balance deltas) right-align
   for place-value comparison; label/identifier/prose columns stay left. */
.vf-explorer-content th.vf-num,
.vf-explorer-content td.vf-num { text-align: right; }
/* Table identifiers cut to a six-glyph handle at EVERY width (operator
   direction 2026-07-17: "no one usually going to grind this much" —
   six symbols identify believably). 7ch = six glyphs + the ellipsis.
   Only the tx detail's fact rows keep the long form — that level IS
   the full-value surface. */
.vf-payer-table .vf-ident,
.vf-tx-inner .vf-ident {
  max-width: 8ch;
}
/* Narrow rung (host width < 620, this element's own measurement): fact
   labels take a fixed compact column (long labels wrap instead of
   starving the value), cell chrome tightens, and the bar charts trade
   height they don't need — the explorer levels carry counts and SOL
   sums, not the main plot's basis-point claims. */
.vf-root.vf-explorer-narrow .vf-fact-label { width: 96px; white-space: normal; }
.vf-root.vf-explorer-narrow .vf-explorer-chart { height: 260px; }
.vf-root.vf-explorer-narrow .vf-explorer-content th,
.vf-root.vf-explorer-narrow .vf-explorer-content td {
  padding-right: 6px;
}
/* Narrow: the tx table trades columns for stacked two-line items — the
   phone table pattern. Column packing at 9-dp precision is a
   font-metrics gamble a real device loses (Galaxy S25U at 360 sheared
   the money columns the test renderer fit, operator 2026-07-17).
   Line 1: signature handle + copy, result at the right edge. Line 2:
   money facts at full precision, each carrying its own written label
   (the thead hides with the columns it described). CU is the one fact
   that stays detail-level-only at this width — served in full as the
   tx detail's "compute units" row (hidden, reachable, never deleted). */
.vf-root.vf-explorer-narrow .vf-tx-inner,
.vf-root.vf-explorer-narrow .vf-tx-inner tbody {
  display: block;
}
.vf-root.vf-explorer-narrow .vf-tx-inner thead {
  display: none;
}
.vf-root.vf-explorer-narrow .vf-tx-inner tr.vf-tx-row {
  display: grid;
  grid-template-columns: 1fr max-content;
  align-items: center;
  gap: 2px 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--vf-border);
}
.vf-root.vf-explorer-narrow .vf-tx-inner td {
  display: block;
  border-bottom: none;
  padding: 0;
}
.vf-root.vf-explorer-narrow .vf-tx-inner td:nth-child(1) {
  grid-column: 1;
  grid-row: 1;
}
.vf-root.vf-explorer-narrow .vf-tx-inner td:nth-child(5) {
  grid-column: 2;
  grid-row: 1;
  justify-self: end;
  white-space: nowrap;
}
.vf-root.vf-explorer-narrow .vf-tx-inner td:nth-child(2) {
  grid-column: 1;
  grid-row: 2;
  text-align: left;
}
.vf-root.vf-explorer-narrow .vf-tx-inner td:nth-child(3) {
  grid-column: 2;
  grid-row: 2;
  justify-self: end;
}
.vf-root.vf-explorer-narrow .vf-tx-inner td:nth-child(4) {
  display: none;
}
.vf-root.vf-explorer-narrow .vf-tx-inner td:nth-child(2)::before {
  content: "fee (SOL) ";
  color: var(--vf-muted);
}
.vf-root.vf-explorer-narrow .vf-tx-inner td:nth-child(3)::before {
  content: "tip (SOL) ";
  color: var(--vf-muted);
}
/* Narrow: pre-formatted blocks (log tail, decoded-instruction JSON)
   wrap instead of dictating the layout's width — a single destination
   address line otherwise forces the whole content area into sideways
   scroll. Content is preserved verbatim, only line breaking changes. */
.vf-root.vf-explorer-narrow .vf-log {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
/* Narrow: the [x] docks to the header corner instead of wrapping onto a
   crumb row of its own (same dock the host widget gives its theme
   toggle on the phone rung). */
.vf-root.vf-explorer-narrow .vf-header {
  position: relative;
  padding-right: 52px;
}
.vf-root.vf-explorer-narrow .vf-explorer-close {
  position: absolute;
  top: 8px;
  right: 4px;
  margin-left: 0;
}
/* Coarse pointer: rows carry a 32px copy chip, so every cell centers on
   the row's axis — top-aligned numerics beside a centered chip read as
   misalignment (operator screenshot 2026-07-17). Fine-pointer keeps the
   settled top alignment. */
@media (pointer: coarse) {
  .vf-explorer-content td { vertical-align: middle; }
}
/* Embedded (nested inside another widget's framed .vf-root): the host card
   already carries the single frame, so this element's own border, radius,
   padding, and panel background are suppressed. Inner layout untouched. */
.vf-root.vf-explorer.vf-embedded { border: none; border-radius: 0; padding: 0; background: transparent; }
`;

/** Per-level payload cache backing theme re-renders without a refetch. */
type Loaded =
  | { kind: "epochs"; points: EpochChannelPoint[] }
  | { kind: "blocks"; epoch: number; blocks: ExplorerBlockRow[] }
  | { kind: "block"; epoch: number; slot: number; data: BlockTxsData; groups: PayerGroup[] }
  | { kind: "tx"; detail: TxDetailData; tipLamports: number };

function parseEpochAttr(value: string | null, name: string): number | undefined {
  if (value === null || value.trim() === "") {
    return undefined;
  }
  const n = Number(value);
  if (!Number.isSafeInteger(n) || n < 0) {
    throw new ApiError(`attribute ${name}: expected a non-negative integer, got "${value}"`);
  }
  return n;
}

function loadingText(level: ExplorerLevel): string {
  switch (level.kind) {
    case "epochs":
      return "Loading income by epoch...";
    case "blocks":
      return `Loading every produced block of epoch ${level.epoch}...`;
    case "block":
      return `Loading stored transactions for slot ${formatCount(level.slot)}...`;
    case "tx":
      return "Loading transaction decode from archival RPC...";
  }
}

export class VfIncomeExplorer extends HTMLElement {
  static readonly observedAttributes = ["api-base", "from-epoch", "to-epoch", "theme", "embedded"];

  #shadow: ShadowRoot | null = null;
  #root: HTMLDivElement | null = null;
  #crumbsEl: HTMLDivElement | null = null;
  #statusEl: HTMLDivElement | null = null;
  #errorEl: HTMLDivElement | null = null;
  #subheadEl: HTMLDivElement | null = null;
  #chartWrapEl: HTMLDivElement | null = null;
  #chartHost: HTMLDivElement | null = null;
  #contentEl: HTMLDivElement | null = null;
  #footnoteEl: HTMLDivElement | null = null;

  #chart: EChartsType | null = null;
  #resizeObserver: ResizeObserver | null = null;
  #abort: AbortController | null = null;
  #seq = 0;
  #level: ExplorerLevel = { kind: "epochs" };
  #loaded: Loaded | null = null;
  #expandedPayers: Set<string> = new Set();
  // Arrow-key bar cursor for the chart levels (epochs, blocks) — the same
  // keyboard pattern as the host widget's plot, so descent past level 1 is
  // not mouse-only. Reset to the newest bar whenever a level's data loads.
  #chartCursor = 0;

  // Tap-away tooltip dismissal, mirroring the host widget: on touch no
  // mouseleave fires and the axis tooltip stayed covering the plot.
  readonly #onDocPointerDownHideTip = (ev: PointerEvent): void => {
    if (this.#chart === null || this.#chartHost === null) {
      return;
    }
    if (ev.composedPath().includes(this.#chartHost)) {
      return;
    }
    this.#chart.dispatchAction({ type: "hideTip" });
  };

  /**
   * One observer, two targets: chart reflow (chart host) and the narrow
   * rung (this element). Below EXPLORER_NARROW_PX the identifier cells
   * switch from wrap-anywhere to ellipsis (see EXPLORER_STYLES). Width 0
   * = unmeasured (tests, pre-layout): stay wide.
   */
  #makeResizeObserver(): ResizeObserver {
    return new ResizeObserver(() => {
      this.#chart?.resize();
      const width = this.clientWidth;
      this.#root?.classList.toggle("vf-explorer-narrow", width > 0 && width < EXPLORER_NARROW_PX);
    });
  }

  connectedCallback(): void {
    this.#renderSkeleton();
    // Reattach path: #renderSkeleton early-returns once built, but
    // disconnectedCallback nulled the observer — recreate it so container
    // resizes keep propagating after a detach/reattach move.
    if (this.#resizeObserver === null && this.#chartHost !== null) {
      this.#resizeObserver = this.#makeResizeObserver();
      this.#resizeObserver.observe(this);
      this.#resizeObserver.observe(this.#chartHost);
    }
    this.#applyThemeClass();
    document.addEventListener("pointerdown", this.#onDocPointerDownHideTip, true);
    void this.#load();
  }

  disconnectedCallback(): void {
    this.#seq += 1;
    this.#abort?.abort();
    this.#abort = null;
    document.removeEventListener("pointerdown", this.#onDocPointerDownHideTip, true);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#chart?.dispose();
    this.#chart = null;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue || this.#root === null) {
      return;
    }
    if (name === "theme") {
      this.#applyThemeClass();
      if (this.#loaded !== null) {
        this.#render();
      }
      return;
    }
    if (name === "embedded") {
      // Frame suppression only — no data dependency, no refetch.
      this.#root?.classList.toggle("vf-embedded", newValue !== null);
      return;
    }
    // Data source changed: any deeper level may no longer exist there.
    this.#level = { kind: "epochs" };
    void this.#load();
  }

  /**
   * Programmatic navigation to one of the four levels; chart clicks, table
   * rows, breadcrumb crumbs, and Esc all route through here. Fetches the
   * level's data on demand and re-renders in place.
   */
  navigateTo(level: ExplorerLevel): void {
    this.#level = level;
    if (level.kind === "block") {
      this.#expandedPayers = new Set();
    }
    void this.#load();
  }

  get #theme(): ThemeTokens {
    return themeTokens(this.getAttribute("theme"));
  }

  #renderSkeleton(): void {
    if (this.#shadow !== null) {
      return;
    }
    this.#shadow = this.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = STYLES + EXPLORER_STYLES;

    const root = document.createElement("div");
    root.className = "vf-root vf-explorer";
    if (this.hasAttribute("embedded")) {
      root.classList.add("vf-embedded");
    }
    root.setAttribute("role", "figure");

    const header = document.createElement("div");
    header.className = "vf-header";
    const title = document.createElement("span");
    title.className = "vf-header-title";
    title.textContent = "Income explorer";
    const sep = document.createElement("span");
    sep.className = "vf-header-sep";
    sep.setAttribute("aria-hidden", "true");
    sep.textContent = "·";
    const crumbs = document.createElement("div");
    crumbs.className = "vf-crumbs";
    crumbs.setAttribute("aria-label", "drill-down breadcrumb");
    const close = document.createElement("button");
    close.type = "button";
    close.className = "vf-btn vf-explorer-close";
    close.classList.add("vf-close");
    close.append(closeIcon());
    close.setAttribute("aria-label", "close income explorer");
    close.addEventListener("click", () => this.#dispatchClose());
    header.append(title, sep, crumbs, close);

    // Live-region roles set once at skeleton build: loading text and
    // error states announce their textContent swaps to AT automatically.
    const errorEl = document.createElement("div");
    errorEl.className = "vf-error vf-hidden";
    errorEl.setAttribute("role", "alert");
    const statusEl = document.createElement("div");
    statusEl.className = "vf-status vf-hidden";
    statusEl.setAttribute("role", "status");
    const subheadEl = document.createElement("div");
    subheadEl.className = "vf-explorer-subhead vf-hidden";
    const chartWrap = document.createElement("div");
    chartWrap.className = "vf-chart-wrap vf-hidden";
    const chartHost = document.createElement("div");
    chartHost.className = "vf-explorer-chart";
    chartHost.tabIndex = 0;
    chartHost.setAttribute("role", "application");
    chartHost.setAttribute("aria-label", "Income chart: Left/Right selects a bar, Enter opens it");
    chartHost.addEventListener("keydown", (ev) => this.#onChartKeydown(ev));
    chartWrap.append(chartHost);
    const contentEl = document.createElement("div");
    contentEl.className = "vf-explorer-content vf-hidden";
    const footnoteEl = document.createElement("div");
    footnoteEl.className = "vf-explorer-footnote vf-hidden";

    root.append(header, errorEl, statusEl, subheadEl, chartWrap, contentEl, footnoteEl);
    this.#shadow.append(style, root);

    this.#root = root;
    this.#crumbsEl = crumbs;
    this.#errorEl = errorEl;
    this.#statusEl = statusEl;
    this.#subheadEl = subheadEl;
    this.#chartWrapEl = chartWrap;
    this.#chartHost = chartHost;
    this.#contentEl = contentEl;
    this.#footnoteEl = footnoteEl;

    this.#resizeObserver = this.#makeResizeObserver();
    // The element itself as a second target of the SAME observer: the
    // chart host is display:none on the table levels, so only the host
    // element reliably reports the width the narrow rung keys off.
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(chartHost);

    // Esc ascends one level; the event is consumed only when handled. At
    // the top level while embedded, "handled" means requesting the close —
    // Esc walks tx -> block -> blocks -> epochs -> chart.
    this.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape") {
        const parent = ascend(this.#level);
        if (parent !== null) {
          ev.stopPropagation();
          this.navigateTo(parent);
        } else if (this.hasAttribute("embedded")) {
          ev.stopPropagation();
          this.#dispatchClose();
        }
      }
    });
  }

  /** Close request shared by the header [x] and top-level Escape; the embedding host routes it to its explorer toggle. */
  #dispatchClose(): void {
    this.dispatchEvent(new CustomEvent(EXPLORER_CLOSE_EVENT, { bubbles: true, composed: true }));
  }

  #applyThemeClass(): void {
    this.#root?.classList.toggle("dark", this.#theme.name === "dark");
  }

  #showStatus(text: string | null): void {
    if (this.#statusEl === null) {
      return;
    }
    this.#statusEl.textContent = text ?? "";
    this.#statusEl.classList.toggle("vf-hidden", text === null);
  }

  /** Error state with the verbatim message and a retry control re-running the current level. */
  #showError(message: string | null): void {
    if (this.#errorEl === null) {
      return;
    }
    this.#errorEl.replaceChildren();
    if (message !== null) {
      const title = document.createElement("div");
      title.className = "vf-error-title";
      title.textContent = "vf-income-explorer: data unavailable";
      const body = document.createElement("div");
      body.textContent = message;
      const retry = document.createElement("button");
      retry.type = "button";
      retry.className = "vf-btn vf-retry";
      retry.textContent = "retry";
      retry.addEventListener("click", () => {
        void this.#load();
      });
      this.#errorEl.append(title, body, retry);
    }
    this.#errorEl.classList.toggle("vf-hidden", message === null);
  }

  /** Blank every level surface so navigation never leaves stale values visible. */
  #clearRenderedData(): void {
    this.#loaded = null;
    this.#chart?.clear();
    this.#chartWrapEl?.classList.add("vf-hidden");
    this.#contentEl?.replaceChildren();
    this.#contentEl?.classList.add("vf-hidden");
    if (this.#subheadEl !== null) {
      this.#subheadEl.textContent = "";
      this.#subheadEl.classList.add("vf-hidden");
    }
    if (this.#footnoteEl !== null) {
      this.#footnoteEl.textContent = "";
      this.#footnoteEl.classList.add("vf-hidden");
    }
  }

  async #load(): Promise<void> {
    const seq = ++this.#seq;
    this.#abort?.abort();
    const abort = new AbortController();
    this.#abort = abort;

    this.#renderCrumbs();
    this.#clearRenderedData();
    this.#showError(null);
    this.#showStatus(loadingText(this.#level));

    try {
      const base = this.getAttribute("api-base");
      if (base === null || base.trim() === "") {
        throw new ApiError('attribute api-base is required (e.g. "https://example.com")');
      }
      const fetchJson = createFetchJson(base.trim());
      const loaded = await this.#fetchLevel(fetchJson, this.#level, abort.signal);
      if (seq !== this.#seq) {
        return;
      }
      this.#loaded = loaded;
      // Keyboard bar cursor starts at the newest bar of the loaded level
      // (reset here, not in #render, so theme re-renders keep the position).
      this.#chartCursor = Math.max(this.#chartBarCount() - 1, 0);
      this.#showStatus(null);
      this.#render();
    } catch (e) {
      if (seq !== this.#seq) {
        return;
      }
      this.#showStatus(null);
      this.#showError(e instanceof Error ? e.message : String(e));
    }
  }

  async #fetchLevel(
    fetchJson: FetchJson,
    level: ExplorerLevel,
    signal: AbortSignal,
  ): Promise<Loaded> {
    switch (level.kind) {
      case "epochs": {
        const fromEpoch = parseEpochAttr(this.getAttribute("from-epoch"), "from-epoch");
        const toEpoch = parseEpochAttr(this.getAttribute("to-epoch"), "to-epoch");
        const params = { from_epoch: fromEpoch, to_epoch: toEpoch };
        const [income, apy] = await Promise.all([
          fetchJson("/income", params, signal).then(parseIncomeRows),
          fetchJson("/apy", params, signal).then(parseApyRows),
        ]);
        return { kind: "epochs", points: joinEpochChannels(income, apy) };
      }
      case "blocks": {
        const blocks = await fetchEpochBlocks(fetchJson, level.epoch, signal);
        return { kind: "blocks", epoch: level.epoch, blocks };
      }
      case "block": {
        const data = parseBlockTxs(await fetchJson(`/blocks/${level.slot}/txs`, {}, signal));
        return {
          kind: "block",
          epoch: level.epoch,
          slot: level.slot,
          data,
          groups: groupByPayer(data.rows),
        };
      }
      case "tx": {
        const detail = parseTxDetail(await fetchJson(`/tx/${level.signature}`, {}, signal));
        return { kind: "tx", detail, tipLamports: level.tipLamports };
      }
    }
  }

  #render(): void {
    this.#renderCrumbs();
    const loaded = this.#loaded;
    if (loaded === null) {
      return;
    }
    switch (loaded.kind) {
      case "epochs":
        this.#renderEpochs(loaded.points);
        break;
      case "blocks":
        this.#renderBlocks(loaded.epoch, loaded.blocks);
        break;
      case "block":
        this.#renderBlock(loaded);
        break;
      case "tx":
        this.#renderTx(loaded.detail, loaded.tipLamports);
        break;
    }
  }

  #renderCrumbs(): void {
    const crumbsEl = this.#crumbsEl;
    if (crumbsEl === null) {
      return;
    }
    crumbsEl.replaceChildren();
    const crumbs = breadcrumbs(this.#level);
    crumbs.forEach((crumb, index) => {
      if (index > 0) {
        const sep = document.createElement("span");
        sep.className = "vf-crumb-sep";
        sep.setAttribute("aria-hidden", "true");
        sep.textContent = ">";
        crumbsEl.append(sep);
      }
      if (crumb.target === null) {
        const current = document.createElement("span");
        current.className = "vf-crumb-current";
        current.setAttribute("aria-current", "page");
        current.textContent = crumb.label;
        crumbsEl.append(current);
      } else {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "vf-btn vf-crumb";
        btn.textContent = crumb.label;
        const target = crumb.target;
        btn.addEventListener("click", () => this.navigateTo(target));
        crumbsEl.append(btn);
      }
    });
  }

  #showAbsence(text: string): void {
    const content = this.#contentEl;
    if (content === null) {
      return;
    }
    const note = document.createElement("div");
    note.className = "vf-absence";
    note.textContent = text;
    content.append(note);
    content.classList.remove("vf-hidden");
  }

  #ensureChart(): EChartsType | null {
    const host = this.#chartHost;
    if (host === null) {
      return null;
    }
    if (this.#chart === null) {
      this.#chart = echarts.init(host, undefined, { renderer: "svg" });
      // Full-column hit area (operator C52): any click inside the plot
      // grid resolves to that column's bar — a short bar alone is
      // untargetable. zrender-level click replaces the series click,
      // which would double-fire when the painted bar itself is hit.
      this.#chart.getZr().on("click", (e: { offsetX: number; offsetY: number }) => {
        const chart = this.#chart;
        const point: [number, number] = [e.offsetX, e.offsetY];
        if (chart === null || !chart.containPixel({ gridIndex: 0 }, point)) {
          return;
        }
        const converted: unknown = chart.convertFromPixel({ gridIndex: 0 }, point);
        const idx = Array.isArray(converted) ? Math.round(Number(converted[0])) : -1;
        if (idx >= 0 && idx < this.#chartBarCount()) {
          this.#onChartClick({ dataIndex: idx });
        }
      });
    }
    return this.#chart;
  }

  /** Bars on the current chart level (epochs, blocks), 0 on the table levels. */
  #chartBarCount(): number {
    const loaded = this.#loaded;
    if (loaded === null) {
      return 0;
    }
    if (loaded.kind === "epochs") {
      return loaded.points.length;
    }
    if (loaded.kind === "blocks") {
      return loaded.blocks.length;
    }
    return 0;
  }

  /**
   * Keyboard access to the chart levels: Left/Right move the bar cursor
   * (with a transient tooltip), Enter descends through the same index path
   * as a chart click. Mirrors the host widget's plot pattern; without it,
   * the keyboard-accessible levels 3-4 sit unreachable behind mouse-only
   * levels 1-2.
   */
  #onChartKeydown(ev: KeyboardEvent): void {
    const count = this.#chartBarCount();
    if (count === 0) {
      return;
    }
    const last = count - 1;
    if (ev.key === "ArrowLeft") {
      ev.preventDefault();
      this.#chartCursor = Math.max(Math.min(this.#chartCursor, last) - 1, 0);
      this.#chart?.dispatchAction({ type: "showTip", seriesIndex: 0, dataIndex: this.#chartCursor });
    } else if (ev.key === "ArrowRight") {
      ev.preventDefault();
      this.#chartCursor = Math.min(this.#chartCursor + 1, last);
      this.#chart?.dispatchAction({ type: "showTip", seriesIndex: 0, dataIndex: this.#chartCursor });
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      this.#onChartClick({ dataIndex: Math.min(this.#chartCursor, last) });
    }
  }

  #onChartClick(params: { dataIndex?: number }): void {
    const loaded = this.#loaded;
    const idx = typeof params.dataIndex === "number" ? params.dataIndex : -1;
    if (loaded === null || idx < 0) {
      return;
    }
    if (loaded.kind === "epochs") {
      const point = loaded.points[idx];
      if (point !== undefined) {
        this.navigateTo({ kind: "blocks", epoch: point.epoch });
      }
    } else if (loaded.kind === "blocks") {
      const block = loaded.blocks[idx];
      if (block !== undefined) {
        this.navigateTo({ kind: "block", epoch: loaded.epoch, slot: block.slot });
      }
    }
  }

  #tooltipFormatter(lines: (idx: number) => { title: string; view: EpochTooltipView } | null) {
    return (params: unknown): string => {
      const first = Array.isArray(params) ? params[0] : params;
      const idx =
        typeof (first as { dataIndex?: number } | undefined)?.dataIndex === "number"
          ? (first as { dataIndex: number }).dataIndex
          : -1;
      const view = idx >= 0 ? lines(idx) : null;
      if (view === null) {
        return "";
      }
      return viewToHtml(view.title, view.view, this.#theme);
    };
  }

  /** Wraps a flat row list into the single-group tooltip view shape. */
  static #rowsView(rows: TooltipLine[]): EpochTooltipView {
    return { groups: [{ label: null, marker: null, rows }], trailing: [] };
  }

  #renderEpochs(points: readonly EpochChannelPoint[]): void {
    if (this.#footnoteEl !== null) {
      this.#footnoteEl.textContent = EPOCHS_FOOTNOTE;
      this.#footnoteEl.classList.remove("vf-hidden");
    }
    this.#root?.setAttribute(
      "aria-label",
      `Income explorer: ${formatCount(points.length)} epochs of income channels.`,
    );
    if (points.length === 0) {
      this.#showAbsence(NO_INCOME_TEXT);
      return;
    }
    const wrap = this.#chartWrapEl;
    const chart = this.#ensureChart();
    if (wrap === null || chart === null) {
      return;
    }
    wrap.classList.remove("vf-hidden");
    const t = this.#theme;
    chart.setOption(
      buildEpochsOption({
        data: points,
        theme: t,
        narrow: this.#root?.classList.contains("vf-explorer-narrow") === true,
        tooltipAppendTo: wrap,
        tooltipFormatter: this.#tooltipFormatter((idx) => {
          const p = points[idx];
          return p === undefined
            ? null
            : { title: epochTooltipTitle(p), view: epochTooltipView(p, t) };
        }),
      }),
      { notMerge: true },
    );
    chart.resize();
  }

  #renderBlocks(epoch: number, blocks: readonly ExplorerBlockRow[]): void {
    const headerLine = blocksHeaderLine(epoch, blocks.length);
    if (this.#subheadEl !== null) {
      this.#subheadEl.textContent = headerLine;
      this.#subheadEl.classList.remove("vf-hidden");
    }
    this.#root?.setAttribute("aria-label", `Income explorer: ${headerLine}.`);
    if (blocks.length === 0) {
      this.#showAbsence(noBlocksText(epoch));
      return;
    }
    const wrap = this.#chartWrapEl;
    const chart = this.#ensureChart();
    if (wrap === null || chart === null) {
      return;
    }
    wrap.classList.remove("vf-hidden");
    const t = this.#theme;
    chart.setOption(
      buildBlocksOption({
        data: blocks,
        theme: t,
        tooltipAppendTo: wrap,
        tooltipFormatter: this.#tooltipFormatter((idx) => {
          const b = blocks[idx];
          return b === undefined
            ? null
            : {
                title: `Slot ${formatCount(b.slot)}`,
                view: VfIncomeExplorer.#rowsView(blockTooltipLines(b, t)),
              };
        }),
      }),
      { notMerge: true },
    );
    chart.resize();
  }

  #renderBlock(loaded: Loaded & { kind: "block" }): void {
    const content = this.#contentEl;
    if (content === null) {
      return;
    }
    content.replaceChildren();
    const line = coverageLine(
      loaded.data.coverage,
      loaded.data.storedTxCount,
      loaded.data.nonVoteTxCount,
    );
    if (this.#subheadEl !== null) {
      this.#subheadEl.textContent = line;
      this.#subheadEl.classList.remove("vf-hidden");
    }
    this.#root?.setAttribute(
      "aria-label",
      `Income explorer: slot ${formatCount(loaded.slot)}, ${line}.`,
    );
    if (loaded.groups.length === 0) {
      this.#showAbsence(NO_STORED_TXS_TEXT);
      return;
    }
    content.append(
      renderPayerTable(loaded.groups, this.#expandedPayers, {
        onTogglePayer: (payer) => {
          if (this.#expandedPayers.has(payer)) {
            this.#expandedPayers.delete(payer);
          } else {
            this.#expandedPayers.add(payer);
          }
          this.#render();
        },
        onTxSelect: (row) => {
          this.navigateTo({
            kind: "tx",
            epoch: loaded.epoch,
            slot: loaded.slot,
            signature: row.signature,
            tipLamports: row.tipLamports,
          });
        },
      }),
    );
    content.classList.remove("vf-hidden");
  }

  #renderTx(detail: TxDetailData, tipLamports: number): void {
    const content = this.#contentEl;
    if (content === null) {
      return;
    }
    this.#root?.setAttribute("aria-label", `Income explorer: transaction ${detail.signature}.`);
    content.replaceChildren(renderTxDetail(detail, tipLamports));
    content.classList.remove("vf-hidden");
  }
}
